import React from "react";
import { APICategory, UnreleasedAPICategory } from "../../../models/Entities";
import {
  Link as LucideLink,
  ArrowLeftRight,
  Code,
  Boxes,
  RefreshCw,
  ArrowUp,
  Webhook,
  Codesandbox,
  Lock,
  Wand2,
} from "lucide-react";
import { DashboardOnboardingStatus, EndpointModelInfo } from "./types";
import {
  ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH,
  ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH,
  ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH,
  ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH,
  ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH,
  ONBOARDING_ADVANCED_SYNCING_DATA_PATH,
  ONBOARDING_ADVANCED_MERGE_WRITES_PATH,
  ONBOARDING_ADVANCED_WEBHOOKS_PATH,
  ONBOARDING_ADVANCED_METADATA_PATH,
  ONBOARDING_ADVANCED_SUPPLEMENTAL_DATA_PATH,
  ONBOARDING_AI_SKILLS_PATH,
  navigateToToggleScopes,
  navigateToSyncSandboxData,
  navigateToSendAPIRequest,
  navigateToIntegrateSyncedData,
  navigateToAddLinkToApp,
  navigateToOnboardingSyncingData,
  navigateToOnboardingMergeWrites,
  navigateToOnboardingWebhooks,
  navigateToOnboardingMetadata,
  navigateToOnboardingSupplementalData,
  navigateToAISkills,
} from "../../../router/RouterUtils";
import MergeLinkIcon from "../../shared/MergeLinkIcon";
import { History } from "history";

export const CategoryPrimaryModelInfo: Record<
  APICategory | UnreleasedAPICategory,
  EndpointModelInfo
> = {
  [APICategory.hris]: {
    modelName: "Employee",
    displayEndpointPath: "hris/v1/employees",
    testEndpointPath: "hris/employees",
  },
  [APICategory.ats]: {
    modelName: "Candidate",
    displayEndpointPath: "ats/v1/candidates",
    testEndpointPath: "ats/candidates",
  },
  [APICategory.accounting]: {
    modelName: "Journal Entry",
    displayEndpointPath: "accounting/v1/journal-entries",
    testEndpointPath: "accounting/journal-entries",
  },
  [APICategory.ticketing]: {
    modelName: "Ticket",
    displayEndpointPath: "ticketing/v1/tickets",
    testEndpointPath: "ticketing/tickets",
  },
  [APICategory.crm]: {
    modelName: "Account",
    displayEndpointPath: "crm/v1/accounts",
    testEndpointPath: "crm/accounts",
  },
  [APICategory.mktg]: {
    modelName: "Campaigns",
    displayEndpointPath: "mktg/v1/campaigns",
    testEndpointPath: "mktg/campaigns",
  },
  [APICategory.filestorage]: {
    modelName: "Files",
    displayEndpointPath: "filestorage/v1/files",
    testEndpointPath: "filestorage/files",
  },
  [UnreleasedAPICategory.datawarehouse]: {
    modelName: "",
    displayEndpointPath: "",
    testEndpointPath: "",
  },
  [APICategory.knowledgebase]: {
    modelName: "",
    displayEndpointPath: "",
    testEndpointPath: "",
  },
  [APICategory.chat]: {
    modelName: "",
    displayEndpointPath: "",
    testEndpointPath: "",
  },
  [UnreleasedAPICategory.email]: {
    modelName: "",
    displayEndpointPath: "",
    testEndpointPath: "",
  },
  [UnreleasedAPICategory.calendar]: {
    modelName: "",
    displayEndpointPath: "",
    testEndpointPath: "",
  },
  [UnreleasedAPICategory.versioncontrol]: {
    modelName: "",
    displayEndpointPath: "",
    testEndpointPath: "",
  },
};

export interface NavStep {
  key: string;
  label: string;
  sidebarTitle: string;
  path: string;
  navigateFn: (history: History) => void;
  icon: JSX.Element;
  statusKey?: keyof DashboardOnboardingStatus;
  dividerAfter?: boolean;
}

export interface NavSection {
  key: "get-started" | "advanced";
  title: string;
  steps: NavStep[];
}

export const NAVIGATION_SECTIONS: NavSection[] = [
  {
    key: "get-started",
    title: "Get started",
    steps: [
      {
        key: "ai_skills",
        label: "AI skills",
        sidebarTitle: "Use AI plugins",
        path: ONBOARDING_AI_SKILLS_PATH,
        navigateFn: navigateToAISkills,
        icon: <Wand2 size={16} />,
        dividerAfter: true,
      },
      {
        key: "toggle_scopes",
        label: "Manage data access",
        sidebarTitle: "Data access",
        path: ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH,
        navigateFn: navigateToToggleScopes,
        icon: <Lock size={16} />,
        statusKey: "has_toggled_scopes",
      },
      {
        key: "sync_sandbox",
        label: "Sync sandbox data",
        sidebarTitle: "Linked Account",
        path: ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH,
        navigateFn: navigateToSyncSandboxData,
        icon: <LucideLink size={16} />,
        statusKey: "has_successfully_linked_account",
      },
      {
        key: "send_api_request",
        label: "Send API request",
        sidebarTitle: "Try Unified API",
        path: ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH,
        navigateFn: navigateToSendAPIRequest,
        icon: <ArrowLeftRight size={16} />,
        statusKey: "has_pulled_common_model_data",
      },
      {
        key: "integrate_synced_data",
        label: "Integrate synced data",
        sidebarTitle: "Connect Unified API",
        path: ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH,
        navigateFn: navigateToIntegrateSyncedData,
        icon: <Code size={16} />,
        statusKey: "has_made_request",
      },
      {
        key: "add_link",
        label: "Add Merge Link to your application",
        sidebarTitle: "Merge Link",
        path: ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH,
        navigateFn: navigateToAddLinkToApp,
        icon: <MergeLinkIcon size={16} />,
        statusKey: "has_opened_production_link",
        dividerAfter: true,
      },
    ],
  },
  {
    key: "advanced",
    title: "Advanced",
    steps: [
      {
        key: "advanced_syncing",
        label: "Syncing data",
        sidebarTitle: "Syncing data",
        path: ONBOARDING_ADVANCED_SYNCING_DATA_PATH,
        navigateFn: navigateToOnboardingSyncingData,
        icon: <RefreshCw size={16} />,
      },
      {
        key: "advanced_merge_writes",
        label: "Writing to Merge",
        sidebarTitle: "Writing to Merge",
        path: ONBOARDING_ADVANCED_MERGE_WRITES_PATH,
        navigateFn: navigateToOnboardingMergeWrites,
        icon: <ArrowUp size={16} />,
      },
      {
        key: "advanced_webhooks",
        label: "Webhooks",
        sidebarTitle: "Webhooks",
        path: ONBOARDING_ADVANCED_WEBHOOKS_PATH,
        navigateFn: navigateToOnboardingWebhooks,
        icon: <Webhook size={16} />,
      },
      {
        key: "advanced_metadata",
        label: "Metadata",
        sidebarTitle: "Metadata",
        path: ONBOARDING_ADVANCED_METADATA_PATH,
        navigateFn: navigateToOnboardingMetadata,
        icon: <Codesandbox size={16} />,
      },
      {
        key: "advanced_supplemental_data",
        label: "Supplemental data",
        sidebarTitle: "Supplemental data",
        path: ONBOARDING_ADVANCED_SUPPLEMENTAL_DATA_PATH,
        navigateFn: navigateToOnboardingSupplementalData,
        icon: <Boxes size={16} />,
      },
    ],
  },
];

const getStartedSection = NAVIGATION_SECTIONS.find((s) => s.key === "get-started");
export const GET_STARTED_PATHS = getStartedSection?.steps.map((step) => step.path) ?? [];
export const GET_STARTED_STEP_PATHS =
  getStartedSection?.steps.filter((step) => step.statusKey).map((step) => step.path) ?? [];

export const ALL_NAV_STEPS = NAVIGATION_SECTIONS.flatMap((section) => section.steps);
